import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon, Cog6ToothIcon, UserGroupIcon, LinkIcon, LockClosedIcon, TagIcon } from '@heroicons/react/24/outline';
import { createArticle, updateArticle, fetchArticleById, fetchArticles, clearCurrentArticle } from '../../store/slices/articleSlice';
import { fetchCategories } from '../../store/slices/categorySlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Button, Input, Select, Card, FileUpload, RichTextEditor, Loading, TagSelector, DynamicSelect } from '../../components/Common';
import AuthorSelector from '../../components/Common/AuthorSelector';
import SEOSettingsModal from '../../components/Modals/SEOSettingsModal';
import RelatedPostsModal from '../../components/Modals/RelatedPostsModal';
import { useOptions } from '../../hooks/useOptions';
import api from '../../services/api';

// Utility function to calculate reading time
const calculateReadingTime = (content) => {
  if (!content || content.trim().length === 0) {
    return '1 min';
  }

  // Remove HTML tags
  const textContent = content.replace(/<[^>]*>/g, ' ');
  
  // Count words (split by whitespace and filter empty strings)
  const words = textContent.trim().split(/\s+/).filter(word => word.length > 0);
  const wordCount = words.length;
  
  // Average reading speed: 200-250 words per minute
  // Using 225 as a middle ground
  const wordsPerMinute = 225;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  
  // Return formatted string
  if (minutes < 1) {
    return '1 min';
  } else if (minutes === 1) {
    return '1 min';
  } else {
    return `${minutes} min`;
  }
};

// Utility function to generate URL-friendly slug from title
const generateSlug = (title) => {
  if (!title || title.trim().length === 0) {
    return '';
  }

  return title
    .toLowerCase()
    .trim()
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove special characters except hyphens
    .replace(/[^a-z0-9-]/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Limit length to 100 characters
    .substring(0, 100);
};

const ArticleForm = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const isView = window.location.pathname.includes('/view/');

  const { currentArticle, loading: articleLoading, articles } = useSelector((state) => state.article);
  const { categories } = useSelector((state) => state.category);

  const [formData, setFormData] = useState({
    title: '',
    brief: '',
    content: '',
    excerpt: '',
    timeToRead: '5 min',
    categoryId: '',
    labelId: '',
    statusId: '1',
    // New fields
    authorIds: [],
    relatedPostIds: [],
    isContentLocked: false,
    publishDate: '',
    // SEO fields
    metaTitle: '',
    metaDescription: '',
    urlSlug: '',
    focusKeyword: '',
    allowIndexing: true,
    robotTags: 'index,follow',
    schemaType: 'Article',
    indexingStatus: 'pending',
    // Image metadata
    coverImageAltText: '',
    // Tags
    tags: [],
    // Access control
    accessType: 'free',
  });
  const [coverImage, setCoverImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSEOModal, setShowSEOModal] = useState(false);
  const [showRelatedPostsModal, setShowRelatedPostsModal] = useState(false);
  const [contentImages, setContentImages] = useState([]); // Track images uploaded in content
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false); // Track if user manually edited slug

  // Use dynamic options hook
  useOptions('articleStatus');
  useOptions('articleLabels');

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchCategories({ size: 100 }));
    // Fetch all articles for related posts selection (no status filter to get all articles)
    dispatch(fetchArticles({ page: 1, size: 100 }));
    if (id) {
      dispatch(fetchArticleById(id));
    }
    return () => dispatch(clearCurrentArticle());
  }, [dispatch, id]);

  // Auto-calculate reading time based on content
  useEffect(() => {
    if (formData.content) {
      const readingTime = calculateReadingTime(formData.content);
      setFormData(prev => ({ ...prev, timeToRead: readingTime }));
    }
  }, [formData.content]);

  // Auto-generate URL slug from title (only if not manually edited)
  useEffect(() => {
    if (formData.title && !slugManuallyEdited && !id) {
      const slug = generateSlug(formData.title);
      setFormData(prev => ({ ...prev, urlSlug: slug }));
    }
  }, [formData.title, slugManuallyEdited, id]);

  useEffect(() => {
    if (currentArticle && id) {
      // Safely extract author IDs from assignedAuthors array
      let authorIds = [];
      if (Array.isArray(currentArticle.assignedAuthors)) {
        authorIds = currentArticle.assignedAuthors.map(author => author?.id).filter(id => id != null);
      } else if (Array.isArray(currentArticle.authorIds)) {
        authorIds = currentArticle.authorIds;
      }
      
      // Safely extract related post IDs
      let relatedPostIds = [];
      if (Array.isArray(currentArticle.relatedPosts)) {
        relatedPostIds = currentArticle.relatedPosts.map(post => post?.id).filter(id => id != null);
      } else if (Array.isArray(currentArticle.relatedPostIds)) {
        relatedPostIds = currentArticle.relatedPostIds;
      }
      
      // Extract tags - article_tag stores tag names as strings
      // TagSelector expects [{id, name}] format
      let tags = [];
      if (Array.isArray(currentArticle.articleTags)) {
        // articleTags contains {id: article_tag.id, tag: "tagName", tag_score: 1}
        // We need to fetch actual tag objects from tags table using tag names
        tags = currentArticle.articleTags
          .filter(at => at?.tag)
          .map(at => ({ name: at.tag }));
      } else if (Array.isArray(currentArticle.tags)) {
        tags = currentArticle.tags;
      }
      
      setFormData({
        title: currentArticle.title || '',
        brief: currentArticle.brief || '',
        content: currentArticle.content || '',
        excerpt: currentArticle.excerpt || '',
        timeToRead: currentArticle.time_to_read || '5 min',
        categoryId: currentArticle.category_id?.toString() || currentArticle.category?.id?.toString() || '',
        labelId: currentArticle.article_label_id?.toString() || currentArticle.label?.id?.toString() || '',
        statusId: currentArticle.status_id?.toString() || currentArticle.status?.id?.toString() || '1',
        authorIds: authorIds,
        relatedPostIds: relatedPostIds,
        tags: tags,
        isContentLocked: currentArticle.is_content_locked || false,
        publishDate: currentArticle.publish_date
          ? (() => {
              const d = new Date(currentArticle.publish_date);
              if (isNaN(d.getTime())) return currentArticle.publish_date;
              return d.toISOString().slice(0, 16);
            })()
          : '',
        metaTitle: currentArticle.meta_title || '',
        metaDescription: currentArticle.meta_description || '',
        urlSlug: currentArticle.url_slug || '',
        focusKeyword: currentArticle.focus_keyword || '',
        allowIndexing: currentArticle.allow_indexing !== false,
        robotTags: currentArticle.robot_tags || 'index,follow',
        schemaType: currentArticle.schema_type || 'Article',
        indexingStatus: currentArticle.indexing_status || 'pending',
        coverImageAltText: currentArticle.cover_image_alt_text || '',
        accessType: currentArticle.access_type || 'free',
      });
      
      if (currentArticle.coverImage?.url) {
        setImagePreview(currentArticle.coverImage.url);
      }
      
      // Fetch full tag objects from tag names
      if (tags.length > 0) {
        fetchTagsFromNames(tags.map(t => t.name));
      }
    }
  }, [currentArticle, id]);
  
  // Helper function to fetch full tag objects from tag names
  const fetchTagsFromNames = async (tagNames) => {
    try {
      const response = await api.get('/admin/tags', {
        params: { size: 100 }
      });
      if (response.data.success) {
        const allTags = response.data.data || [];
        // Match tag names to get full tag objects with IDs
        const matchedTags = tagNames
          .map(name => allTags.find(t => t.name === name))
          .filter(t => t != null);
        setFormData(prev => ({ ...prev, tags: matchedTags }));
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    // Clear error for this field when user changes it
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({ ...prev, content: value }));
    if (errors.content) setErrors(prev => ({ ...prev, content: null }));
  };

  const handleRelatedPostChange = (postId) => {
    if (formData.relatedPostIds.length >= 3 && !formData.relatedPostIds.includes(postId)) {
      return; // Max 3 related posts
    }
    setFormData(prev => ({
      ...prev,
      relatedPostIds: prev.relatedPostIds.includes(postId)
        ? prev.relatedPostIds.filter(id => id !== postId)
        : [...prev.relatedPostIds, postId]
    }));
  };

  const handleSEOSave = (seoData) => {
    setFormData(prev => ({
      ...prev,
      metaTitle: seoData.metaTitle,
      metaDescription: seoData.metaDescription,
      urlSlug: seoData.urlSlug,
      focusKeyword: seoData.focusKeyword,
      allowIndexing: seoData.allowIndexing,
      robotTags: seoData.robotTags,
      schemaType: seoData.schemaType,
      indexingStatus: seoData.indexingStatus,
    }));
  };

  const validate = (isDraft = false) => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    
    if (!isDraft) {
      if (!formData.brief.trim()) newErrors.brief = 'Brief description is required';
      if (formData.brief.length > 1000) newErrors.brief = 'Brief description must not exceed 1000 characters';
      if (!formData.content.trim()) newErrors.content = 'Content is required';
      if (!formData.excerpt.trim()) newErrors.excerpt = 'Excerpt is required';
      if (!formData.categoryId) newErrors.categoryId = 'Category is required';
      if (!coverImage && !imagePreview) newErrors.coverImage = 'Cover image is required';
      if (!formData.urlSlug.trim()) newErrors.urlSlug = 'URL slug is required';
      if (formData.authorIds.length === 0) newErrors.authorIds = 'At least one author is required';
      if (formData.tags.length === 0) newErrors.tags = 'At least one tag is required';
    }
    
    if (!formData.statusId) newErrors.statusId = 'Status is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, isDraft = false) => {
    if (e) e.preventDefault();
    if (!validate(isDraft)) return;

    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('brief', formData.brief || '');
      submitData.append('content', formData.content || '');
      submitData.append('excerpt', formData.excerpt || '');
      submitData.append('timeToRead', formData.timeToRead);
      submitData.append('categoryId', formData.categoryId || '');
      submitData.append('labelId', formData.labelId || '');
      submitData.append('statusId', isDraft ? '3' : formData.statusId);
      if (coverImage) submitData.append('coverImage', coverImage);
      
      // New fields
      submitData.append('authorIds', JSON.stringify(formData.authorIds));
      submitData.append('relatedPostIds', JSON.stringify(formData.relatedPostIds));
      submitData.append('isContentLocked', formData.isContentLocked);
      if (formData.publishDate) submitData.append('publishDate', formData.publishDate);
      
      // SEO fields
      submitData.append('metaTitle', formData.metaTitle);
      submitData.append('metaDescription', formData.metaDescription);
      submitData.append('urlSlug', formData.urlSlug || generateSlug(formData.title));
      submitData.append('focusKeyword', formData.focusKeyword);
      submitData.append('allowIndexing', formData.allowIndexing);
      submitData.append('robotTags', formData.robotTags);
      submitData.append('schemaType', formData.schemaType);
      submitData.append('indexingStatus', formData.indexingStatus);
      
      // Access control
      submitData.append('accessType', formData.accessType);
      
      // Image metadata
      if (formData.coverImageAltText) {
        submitData.append('coverImageAltText', formData.coverImageAltText);
      }
      
      // Content images metadata (for future use)
      if (contentImages.length > 0) {
        submitData.append('contentImages', JSON.stringify(contentImages));
      }
      
      // Tags - ensure IDs are resolved before sending
      let tagIdsToSubmit = [];
      if (formData.tags.length > 0) {
        const tagsHaveIds = formData.tags.every(t => t.id != null);
        if (tagsHaveIds) {
          tagIdsToSubmit = formData.tags.map(t => t.id).filter(id => id != null);
        } else {
          // Tags loaded with names only (before fetchTagsFromNames completed) — look up IDs now
          const tagNames = formData.tags.map(t => t.name || t).filter(Boolean);
          try {
            const resp = await api.get('/admin/tags', { params: { size: 200 } });
            if (resp.data.success) {
              const allTags = resp.data.data || [];
              tagIdsToSubmit = tagNames
                .map(name => allTags.find(t => t.name === name))
                .filter(t => t != null)
                .map(t => t.id);
            }
          } catch (_) {
            console.error('Failed to resolve tag IDs');
          }
        }
      }
      submitData.append('tagIds', JSON.stringify(tagIdsToSubmit));

      if (isEdit) {
        await dispatch(updateArticle({ id, data: submitData })).unwrap();
        dispatch(showSuccess(isDraft ? 'Draft saved successfully' : 'Article updated successfully'));
      } else {
        await dispatch(createArticle(submitData)).unwrap();
        dispatch(showSuccess(isDraft ? 'Draft saved successfully' : 'Article created successfully'));
      }
      navigate('/articles');
    } catch (error) {
      console.error('Submit error:', error);
      // Show backend validation errors on form fields if available
      if (error?.errors) {
        setErrors(prev => ({ ...prev, ...error.errors }));
      }
      dispatch(showError(error?.message || error || 'Failed to save article'));
    } finally {
      setSubmitting(false);
    }
  };

  // Get available articles for related posts (exclude current)
  const availableRelatedPosts = (articles || []).filter(a => a.id?.toString() !== id);

  // Show loading state while fetching article
  if (articleLoading && id) return <Loading fullScreen text="Loading article..." />;
  
  // Show error if article not found when editing
  if (id && !articleLoading && !currentArticle) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <div className="text-center py-8">
            <DocumentTextIcon className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Article Not Found</h2>
            <p className="text-gray-600 mb-4">The article you're trying to edit could not be found.</p>
            <Button onClick={() => navigate('/articles')}>
              Back to Articles
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/articles')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              {isView ? 'View Article' : isEdit ? 'Edit Article' : 'Create Article'}
            </h1>
            <p className="text-slate-500">
              {isView ? 'Article details' : isEdit ? 'Update article information' : 'Add a new article to your platform'}
            </p>
          </div>
        </div>
        
        {/* SEO Settings Button */}
        {!isView && (
          <Button 
            variant="secondary" 
            onClick={() => setShowSEOModal(true)}
            className="flex items-center gap-2"
          >
            <Cog6ToothIcon className="w-5 h-5" />
            SEO Settings
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Article Details */}
            <Card title="Article Details" icon={DocumentTextIcon}>
              <div className="space-y-5">
                <Input
                  label="Title *"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter article title"
                  error={errors.title}
                  disabled={isView}
                  required
                />

                <div>
                  <div className="flex justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Brief Description <span className="text-red-500">*</span>
                    </label>
                    <span className={`text-xs ${formData.brief.length > 1000 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                      {formData.brief.length}/1000
                    </span>
                  </div>
                  <textarea
                    name="brief"
                    value={formData.brief}
                    onChange={(e) => {
                      if (e.target.value.length <= 1000) {
                        handleChange(e);
                      }
                    }}
                    placeholder="Short description of the article (max 1000 characters)"
                    rows={4}
                    maxLength={1000}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                      errors.brief ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isView}
                  />
                  {errors.brief && (
                    <p className="mt-1 text-sm text-red-600">{errors.brief}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Keep it concise - this appears in article previews and search results
                  </p>
                </div>

                {/* URL Slug */}
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      URL Slug (SEO-friendly URL) <span className="text-red-500">*</span>
                    </label>
                    <span className="text-xs text-gray-500">
                      {formData.urlSlug.length}/100
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm whitespace-nowrap">
                      studentsherald.com/article/
                    </span>
                    <input
                      type="text"
                      name="urlSlug"
                      value={formData.urlSlug}
                      onChange={(e) => {
                        const slug = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, '')
                          .substring(0, 100);
                        setFormData(prev => ({ ...prev, urlSlug: slug }));
                        setSlugManuallyEdited(true);
                      }}
                      placeholder="auto-generated-from-title"
                      maxLength={100}
                      className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                        errors.urlSlug ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={isView}
                    />
                    {!isView && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => {
                            const slug = generateSlug(prev.title);
                            return { ...prev, urlSlug: slug };
                          });
                          setSlugManuallyEdited(false);
                        }}
                        className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
                        title="Regenerate from title"
                      >
                        🔄 Regenerate
                      </button>
                    )}
                  </div>
                  {errors.urlSlug && (
                    <p className="mt-1 text-sm text-red-600">{errors.urlSlug}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {slugManuallyEdited ? (
                      <span className="text-blue-600">✏️ Manually edited - won't auto-update</span>
                    ) : (
                      <span>🤖 Auto-generated from title - edit to customize</span>
                    )}
                  </p>
                </div>

                <RichTextEditor
                  label="Content *"
                  value={formData.content}
                  onChange={handleContentChange}
                  onImageUpload={(imageData) => {
                    // Track images uploaded in content for metadata
                    setContentImages(prev => [...prev, imageData]);
                  }}
                  placeholder="Write your article content here..."
                  error={errors.content}
                  minHeight={300}
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  💡 <strong>Tip:</strong> Click the image icon to upload images. They'll be automatically uploaded to the server (max 5MB each).
                </p>
              </div>
            </Card>

            {/* Cover Image */}
            <Card title="Cover Image *">
              <FileUpload
                label="Upload cover image *"
                accept="image/*"
                maxSize={2}
                minWidth={800}
                minHeight={400}
                recommendedWidth={1200}
                recommendedHeight={630}
                onChange={(file) => {
                  setCoverImage(file);
                  // Clear preview when file is removed
                  if (!file) {
                    setImagePreview(null);
                  } else {
                    // Create preview for new file
                    const reader = new FileReader();
                    reader.onload = (e) => setImagePreview(e.target.result);
                    reader.readAsDataURL(file);
                  }
                }}
                onMetadataChange={(metadata) => {
                  setFormData(prev => ({ ...prev, coverImageAltText: metadata.altText || '' }));
                }}
                preview={imagePreview}
                showAltText={true}
                altText={formData.coverImageAltText}
                required
                error={errors.coverImage}
              />
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>💡 Image Guidelines:</strong>
                </p>
                <ul className="text-xs text-blue-700 mt-1 ml-4 space-y-1">
                  <li>• Max size: 2MB for optimal performance</li>
                  <li>• Recommended: 1200×630px (perfect for social sharing)</li>
                  <li>• Minimum: 800×400px</li>
                  <li>• Formats: JPG, PNG, WebP</li>
                  <li>• Always add alt text for SEO & accessibility</li>
                </ul>
              </div>
            </Card>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <Card title="Publish Settings">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Publish date
                  </label>
                  <Input
                    type="datetime-local"
                    name="publishDate"
                    value={formData.publishDate}
                    onChange={handleChange}
                    disabled={isView}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to publish now</p>
                </div>

                <DynamicSelect
                  optionType="articleStatus"
                  label="Status *"
                  name="statusId"
                  value={formData.statusId}
                  onChange={handleChange}
                  placeholder="Select status"
                  disabled={isView}
                  required
                  error={errors.statusId}
                />

                <Select
                  label="Category *"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  options={categories.map(cat => ({ value: cat.id.toString(), label: cat.name }))}
                  placeholder="Select category"
                  disabled={isView}
                  required
                  error={errors.categoryId}
                />

                <DynamicSelect
                  optionType="articleLabels"
                  label="Label (Optional)"
                  name="labelId"
                  value={formData.labelId}
                  onChange={handleChange}
                  placeholder="Select label (optional)"
                  disabled={isView}
                  error={errors.labelId}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time to Read (Auto-calculated)
                  </label>
                  <Input
                    name="timeToRead"
                    value={formData.timeToRead}
                    readOnly
                    disabled
                    className="bg-gray-50 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    📖 Automatically calculated based on word count (~225 words/min)
                  </p>
                </div>
              </div>
            </Card>

            {/* Writer/Author Selection */}
            <Card title="Writer *" icon={UserGroupIcon}>
              <AuthorSelector
                value={formData.authorIds}
                onChange={(authorIds) => setFormData(prev => ({ ...prev, authorIds }))}
                label=""
                placeholder="Select one or more authors"
                multiple={true}
                required={true}
                error={errors.authorIds}
              />
              {errors.authorIds && (
                <p className="mt-1 text-sm text-red-600">{errors.authorIds}</p>
              )}
            </Card>

            {/* Excerpt */}
            <Card title="Excerpt *">
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700">Article excerpt *</label>
                  <span className="text-xs text-gray-500">{formData.excerpt.length}/500</span>
                </div>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  placeholder="e.g., This quick and easy pasta dish is great for busy weeknight family meals."
                  rows={3}
                  maxLength={500}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                    errors.excerpt ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isView}
                  required
                />
                {errors.excerpt && (
                  <p className="mt-1 text-sm text-red-600">{errors.excerpt}</p>
                )}
              </div>
            </Card>

            {/* Tags */}
            <Card title="Tags *" icon={TagIcon}>
              <TagSelector
                selectedTags={formData.tags}
                onChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                maxTags={10}
                disabled={isView}
                required
              />
              {errors.tags && (
                <p className="mt-1 text-sm text-red-600">{errors.tags}</p>
              )}
            </Card>

            {/* Related Posts */}
            <Card title="Related posts" icon={LinkIcon}>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Choose up to 3 related posts</p>
                  <span className="text-xs text-gray-500">{formData.relatedPostIds.length}/3</span>
                </div>
                
                {formData.relatedPostIds.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.relatedPostIds.map(postId => {
                      const post = availableRelatedPosts.find(p => p.id?.toString() === postId);
                      return post ? (
                        <div key={postId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm truncate flex-1">{post.title}</span>
                          <button
                            type="button"
                            onClick={() => handleRelatedPostChange(postId)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            ×
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
                
                <button
                  type="button"
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowRelatedPostsModal(true)}
                  disabled={isView}
                >
                  + Choose Posts
                </button>
              </div>
            </Card>

            {/* Content Lock */}
            <Card title="Content Lock" icon={LockClosedIcon}>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Type
                  </label>
                  <select
                    name="accessType"
                    value={formData.accessType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isView}
                  >
                    <option value="free">Free - Anyone can read</option>
                    <option value="registered">Registered - Login required</option>
                    <option value="subscribed">Subscribed - Subscription required</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Control who can access this content
                  </p>
                </div>
                
                {!isEdit && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isContentLocked"
                      checked={formData.isContentLocked}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                      disabled={isView}
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Lock this content</span>
                      <p className="text-xs text-gray-500">
                        Require users to register/subscribe to read full article
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </Card>

            {/* SEO Preview */}
            <Card title="SEO Settings">
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Meta Title</label>
                  <p className="text-sm text-gray-700 truncate">
                    {formData.metaTitle || formData.title || 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Focus Keyword</label>
                  <p className="text-sm text-gray-700">
                    {formData.focusKeyword || 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">URL Slug</label>
                  <p className="text-sm text-gray-700 truncate">
                    /{formData.urlSlug || 'auto-generated'}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => setShowSEOModal(true)}
                  type="button"
                >
                  Edit SEO Settings
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {/* Actions */}
        {!isView && (
          <div className="flex items-center justify-end gap-3 pt-6 border-t">
            <Button variant="secondary" type="button" onClick={() => navigate('/articles')}>
              Cancel
            </Button>
            <Button variant="outline" type="button" onClick={(e) => handleSubmit(e, true)}>
              Save as Draft
            </Button>
            <Button type="submit" loading={submitting}>
              {isEdit ? 'Update Article' : 'Publish Article'}
            </Button>
          </div>
        )}
      </form>

      {/* SEO Settings Modal */}
      <SEOSettingsModal
        isOpen={showSEOModal}
        onClose={() => setShowSEOModal(false)}
        articleData={formData}
        onSave={handleSEOSave}
      />

      {/* Related Posts Modal */}
      <RelatedPostsModal
        isOpen={showRelatedPostsModal}
        onClose={() => setShowRelatedPostsModal(false)}
        articles={availableRelatedPosts}
        selectedIds={formData.relatedPostIds}
        onSave={(ids) => setFormData(prev => ({ ...prev, relatedPostIds: ids }))}
      />
    </div>
  );
};

export default ArticleForm;
